interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

interface Entry {
  id: string;
  guest_name: string;
  message: string | null;
  emoji: string | null;
  pos_x: number;
  pos_y: number;
  signature_data: string | null;
  photo_url: string | null;
  photo_id: string | null;
  status: string;
  created_at: string;
}

interface SignatureBook {
  title: string;
  event_date: string | null;
  cover_photo_url: string | null;
  closing_message: string | null;
}

interface Props {
  book: SignatureBook;
  photos: Photo[];
  entries: Entry[];
}

const ACCENT = "#C8A96E";

export default function PDFPrintTab({ book, photos, entries }: Props) {
  const visible = entries.filter((e) => e.status === "visible");

  const getEntriesForPhoto = (photoId: string) =>
    visible.filter((e) => e.photo_id === photoId);

  const freeEntries = visible.filter((e) => !e.photo_id);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print actions — screen only */}
      <div className="print:hidden space-y-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
              <i className="ri-file-pdf-2-line text-lg"></i>
            </div>
            <div>
              <p className="text-slate-800 font-semibold text-sm">Descargar / Imprimir</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Se generará el libro con portada, fotos y mensajes de todos los invitados
              </p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {[
              { icon: "ri-image-2-line",    label: `${photos.length} fotos` },
              { icon: "ri-message-3-line",  label: `${visible.length} mensajes de invitados` },
              { icon: "ri-quill-pen-line",  label: `${visible.filter((e) => e.signature_data).length} firmas digitales` },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-xs text-slate-500">
                <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                  <i className={item.icon}></i>
                </span>
                {item.label}
              </div>
            ))}
          </div>

          <button
            onClick={handlePrint}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all active:scale-95"
            style={{ backgroundColor: ACCENT, color: "#fff" }}
          >
            <i className="ri-download-2-line text-base"></i>
            Descargar PDF / Imprimir
          </button>

          <p className="text-xs text-slate-400 mt-2 text-center">
            En el diálogo de impresión elige &quot;Guardar como PDF&quot;
          </p>
        </div>

        {/* Preview of what will be printed */}
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
          <p className="text-amber-700 font-semibold text-sm mb-2 flex items-center gap-2">
            <i className="ri-eye-line"></i>
            Vista previa del PDF
          </p>
          <p className="text-amber-600 text-xs leading-relaxed">
            El PDF incluirá: portada del libro, cada foto con sus mensajes superpuestos, y al final la lista completa de mensajes con firmas.
          </p>
        </div>
      </div>

      {/* ── PRINTABLE CONTENT ── */}
      <div className="hidden print:block">
        <style>{`
          @media print {
            @page { margin: 0; size: A4 portrait; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .print-break { page-break-after: always; break-after: page; }
            .print-break-inside { break-inside: avoid; }
          }
        `}</style>

        {/* Cover page */}
        <div
          className="print-break w-full h-screen flex flex-col items-center justify-center text-center p-12 relative"
          style={{
            backgroundColor: book.cover_photo_url ? "#000" : "#FAF8F4",
            minHeight: "29.7cm",
          }}
        >
          {book.cover_photo_url && (
            <img
              src={book.cover_photo_url}
              alt="portada"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          )}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.65))" }}
          />
          <div className="relative z-10 space-y-4">
            <div
              className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto"
              style={{ backgroundColor: ACCENT + "30", border: `2px solid ${ACCENT}60` }}
            >
              <i className="ri-book-open-line text-3xl" style={{ color: ACCENT }}></i>
            </div>
            <p
              className="text-xs uppercase tracking-[0.4em] font-semibold"
              style={{ color: "rgba(255,255,255,0.65)" }}
            >
              Libro de Firmas
            </p>
            <h1
              className="text-4xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {book.title}
            </h1>
            {book.event_date && (
              <p className="text-lg" style={{ color: "rgba(255,255,255,0.75)" }}>
                {new Date(book.event_date).toLocaleDateString("es-MX", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            )}
            <div
              className="w-24 h-px mx-auto mt-4"
              style={{ backgroundColor: ACCENT }}
            />
            <p style={{ color: ACCENT, fontSize: "13px" }}>
              {visible.length} mensajes de invitados
            </p>
          </div>
        </div>

        {/* Photo pages with stickers */}
        {photos.map((photo) => {
          const photoEntries = getEntriesForPhoto(photo.id);
          return (
            <div
              key={photo.id}
              className="print-break relative w-full"
              style={{ minHeight: "22cm", backgroundColor: "#0f172a" }}
            >
              <img
                src={photo.photo_url}
                alt=""
                className="w-full"
                style={{ maxHeight: "22cm", objectFit: "cover", objectPosition: "center top", display: "block" }}
              />
              {photoEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="absolute"
                  style={{
                    left:      `${Math.min(Math.max(entry.pos_x, 0), 80)}%`,
                    top:       `${Math.min(Math.max(entry.pos_y, 0), 80)}%`,
                    transform: "translate(-50%, -50%)",
                    maxWidth:  "180px",
                    zIndex:    10,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      borderRadius: "12px",
                      padding: "10px 12px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                      maxWidth: "180px",
                      fontSize: "11px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                      {entry.emoji && <span style={{ fontSize: "14px" }}>{entry.emoji}</span>}
                      <strong style={{ color: "#1e293b", fontSize: "11px" }}>{entry.guest_name}</strong>
                    </div>
                    {entry.message && (
                      <p style={{ color: "#475569", fontSize: "10px", lineHeight: "1.4", margin: 0 }}>
                        {entry.message}
                      </p>
                    )}
                    {entry.signature_data && (
                      <img
                        src={entry.signature_data}
                        alt="firma"
                        style={{ marginTop: "6px", height: "28px", objectFit: "contain", display: "block" }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {/* Messages list page */}
        {visible.length > 0 && (
          <div
            className="print-break p-12"
            style={{ backgroundColor: "#FAF8F4", minHeight: "29.7cm" }}
          >
            <h2
              className="text-3xl font-bold mb-2"
              style={{ color: "#1C1008", fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Mensajes de nuestros invitados
            </h2>
            <div
              className="w-16 h-0.5 mb-8"
              style={{ backgroundColor: ACCENT }}
            />
            <div className="grid grid-cols-2 gap-4">
              {visible.map((entry) => (
                <div
                  key={entry.id}
                  className="print-break-inside rounded-xl p-4 border"
                  style={{ borderColor: "#e8e0d0", backgroundColor: "#fff" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                    {entry.emoji && <span style={{ fontSize: "16px" }}>{entry.emoji}</span>}
                    <strong style={{ color: "#1C1008", fontSize: "13px" }}>{entry.guest_name}</strong>
                  </div>
                  {entry.message && (
                    <p style={{ color: "#5C4030", fontSize: "12px", lineHeight: "1.6", fontStyle: "italic" }}>
                      &ldquo;{entry.message}&rdquo;
                    </p>
                  )}
                  {entry.signature_data && (
                    <img
                      src={entry.signature_data}
                      alt="firma"
                      style={{ marginTop: "8px", height: "32px", objectFit: "contain", display: "block" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Free entries page */}
        {freeEntries.length > 0 && (
          <div className="p-12" style={{ backgroundColor: "#FAF8F4" }}>
            <h3
              className="text-xl font-bold mb-6"
              style={{ color: "#1C1008", fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Mensajes adicionales
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {freeEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="print-break-inside rounded-xl p-3 border"
                  style={{ borderColor: "#e8e0d0", backgroundColor: "#fff" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                    {entry.emoji && <span style={{ fontSize: "14px" }}>{entry.emoji}</span>}
                    <strong style={{ color: "#1C1008", fontSize: "12px" }}>{entry.guest_name}</strong>
                  </div>
                  {entry.message && (
                    <p style={{ color: "#5C4030", fontSize: "11px", lineHeight: "1.5", fontStyle: "italic" }}>
                      &ldquo;{entry.message}&rdquo;
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Closing message */}
            {book.closing_message && (
              <div
                className="mt-10 text-center"
                style={{ borderTop: `2px solid ${ACCENT}30`, paddingTop: "40px" }}
              >
                <p
                  className="text-xl italic"
                  style={{
                    color: ACCENT,
                    fontFamily: "'Playfair Display', Georgia, serif",
                    lineHeight: "1.8",
                  }}
                >
                  &ldquo;{book.closing_message}&rdquo;
                </p>
              </div>
            )}
          </div>
        )}

        {/* Closing page if no free entries */}
        {freeEntries.length === 0 && book.closing_message && (
          <div
            className="flex items-center justify-center p-12"
            style={{ backgroundColor: "#FAF8F4", minHeight: "14cm" }}
          >
            <div className="text-center max-w-md">
              <div
                className="w-12 h-0.5 mx-auto mb-8"
                style={{ backgroundColor: ACCENT }}
              />
              <p
                className="text-2xl italic"
                style={{
                  color: ACCENT,
                  fontFamily: "'Playfair Display', Georgia, serif",
                  lineHeight: "1.8",
                }}
              >
                &ldquo;{book.closing_message}&rdquo;
              </p>
              <div
                className="w-12 h-0.5 mx-auto mt-8"
                style={{ backgroundColor: ACCENT }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}